<template>
  <PageContainer title="Listar Usuários">
    <div v-if="!loaded" class="d-flex justify-content-center">
      <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
      </component>
    </div>

    <Box v-else class="usuarios-container" space="medium">
      <form @submit.prevent="handleSubmit">
        <div class="inputs">
          <div class="inputs-row">
            <Select v-if="idRole == 2" class="input" :options="schemaOptions" legend="Auto escola"
              placeholder="Selecione uma auto escola" :modelValue="schema"
              @update:modelValue="$event => (schema = $event)" :disabled="loadingAluno || loading" />

            <Input class="input" :modelValue="nome" @update:modelValue="$event => (this.nome = $event)" legend="Nome"
              placeholder="Nome" :disabled="loading" />

            <Input class="input" mask="###.###.###-##" :modelValue="cpf"
              @update:modelValue="$event => (this.cpf = $event)" legend="CPF" placeholder="CPF" :disabled="loading" />
          </div>
          <div class="inputs-row">
            <Select class="input" :options="funcoes" legend="Função" placeholder="Função" :modelValue="funcao"
              @update:modelValue="$event => (this.funcao = $event)" :disabled="loading" />

            <Select class="input" :options="statusOptions" legend="Status" placeholder="Status" :modelValue="status"
              @update:modelValue="$event => (this.status = $event)" :disabled="loading" />

            <Period class="input" legend="Data" :firstModelValue="initialDate" :secondModelValue="finalDate"
              @update:firstModelValue="$event => (this.initialDate = $event)"
              @update:secondModelValue="$event => (this.finalDate = $event)" :disabled="loading" />
          </div>
        </div>

        <div class="buttons">
          <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
            :disabled="loading">
            <CloseCircleOutline :size="18" />
          </Button>

          <Button type="submit" text="Consultar" color="primary" :disabled="loading">
            <Magnify :size="18" />
          </Button>
        </div>
      </form>

      <div v-if="loading" class="d-flex justify-content-center">
        <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
        </component>
      </div>

      <template v-else-if="usuarios.length > 0 && !loading">
        <div class="table-container">
          <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
            @page-changed="fetchUsuarios" />

          <Table :headers="['Status', 'Auto Escola', 'CPF', 'Nome', 'Função', 'Data de Criação', 'Ações']">
            <tr v-for="usuario in usuarios" :key="usuario.id_aluno">
              <td>
                <Badge v-if="usuario.flg_ativo" text="Ativo" color="success" />
                <Badge v-else text="Inativo" color="danger" />
              </td>
              <td>{{ usuario.nome_fantasia || String(usuario.schema).toLocaleUpperCase() }}</td>
              <td>{{ utils.formatarCPF(usuario.cpf) }}</td>
              <td>{{ usuario.nome }}</td>
              <td>{{ usuario.funcao_usuario }}</td>
              <td>{{ utils.formatOnlyDate(usuario.data_criacao) }}</td>
              <td>
                <div class="actions">
                  <div v-if="usuario.flg_ativo">
                    <IconButton @handle:click="updateStatus(usuario.id_usuario, 'desativar')" color="danger"
                      title="Desativar usuário" :disabled="loadingActions">
                      <LockOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-else>
                    <IconButton @handle:click="updateStatus(usuario.id_usuario, 'ativar')" color="success"
                      title="Ativar usuário" :disabled="loadingActions">
                      <LockOpenOutline :size="19" />
                    </IconButton>
                  </div>
                  <div>
                    <IconButton @handle:click="openInfoModal(usuario.id_usuario)" color="secondary"
                      title="Ver informações do usuário" :disabled="loadingActions">
                      <InformationOutline :size="19" />
                    </IconButton>
                  </div>
                  <div>
                    <IconButton @handle:click="toEdit(usuario.id_usuario)" color="primary" title="Atualizar usuário"
                      :disabled="!usuario.flg_ativo || loadingActions">
                      <PencilOutline :size="19" />
                    </IconButton>
                  </div>
                </div>
              </td>
            </tr>
          </Table>
        </div>

        <div class="actions">
          <Button type="button" @handle:click="openExportModal" text="Baixar relatório de usuários" :fill="false"
            color="primary" :disabled="loading">
            <TrayArrowDown :size="18" />
          </Button>
        </div>
      </template>
    </Box>

    <InfoModal ref="infoModal" />

    <ExportModal ref="exportModal" type="reduced" @submit:export="submitExport" :loading="exportLoading" />
  </PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import utils from '../../services/utils'
import urlApi from '../../router/urlApi'
import urlApiV2 from '../../router/urlApiV2'
import _export from '../../services/export'
import PageContainer from '../core/PageContainer'
import Box from '../core/Box'
import Button from '../core/Button'
import Input from '../core/Input'
import Select from '../core/Select'
import Period from '../core/Period'
import Badge from '../core/Badge'
import Table from '../core/Table'
import Paginate from '../core/Paginate'
import IconButton from '../core/IconButton'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import TrayArrowDown from 'icons/TrayArrowDown'
import LockOutline from 'icons/LockOutline'
import LockOpenOutline from 'icons/LockOpenOutline'
import InformationOutline from 'icons/InformationOutline'
import PencilOutline from 'icons/PencilOutline'
import InfoModal from './InfoModal'
import ExportModal from '../export/ExportModal'

export default {
  name: 'ListagemUsuarios',
  components: {
    ...spinners,
    CloseCircleOutline,
    Magnify,
    TrayArrowDown,
    LockOutline,
    LockOpenOutline,
    InformationOutline,
    PencilOutline,

    PageContainer,
    Box,
    Button,
    Input,
    Select,
    Period,
    Badge,
    Table,
    Paginate,
    IconButton,
    InfoModal,
    ExportModal,
  },
  data() {
    return {
      idRole: Number(localStorage.id_role),
      utils: utils,
      nome: '',
      cpf: '',
      funcao: '',
      status: '',
      initialDate: '',
      finalDate: '',
      funcoes: [],
      statusOptions: [
        { value: 'active', text: 'Ativo' },
        { value: 'inactive', text: 'Inativo' },
      ],
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      filters: {},
      usuarios: [],
      loaded: false,
      loading: false,
      loadingActions: false,
      exportLoading: false,
      schema: null,
      schemaOptions: [],
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchCfcs(),
      this.loadRoles()
    ])

    if (this.idRole == 1) {
      this.schema = localStorage.schema
    }

    this.loaded = true
  },
  methods: {
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async loadRoles() {
      await this.$http.get(`${urlApiV2}/usuarios/funcoes`)
        .then(response => {
          this.funcoes = response.body.dados.map(item => {
            return {
              value: item.id_funcao_usuario,
              text: item.funcao_usuario,
            }
          })
        })
        .catch(err => {
          this.$toasted.show('Erro ao carregar as funções de usuário.', {
            position: 'top-right',
          })
        })
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        this.fetchUsuarios()
      }
    },
    validateInputs() {
      if (this.idRole === 2 && !this.schema) {
        this.toast('Selecione uma auto escola.')
        
        return false
      }

      if (this.cpf && this.cpf.length < 11) {
        this.toast('CPF Inválido.')
        return false
      }

      const initial = new Date(this.initialDate);
      const final = new Date(this.finalDate);

      if (initial > final) {
        this.toast('A data inicial deve ser menor que a data final!')
        return false
      }

      if (final < initial) {
        this.toast('A data final deve ser menor que a data inicial!')
        return false
      }

      return true
    },
    async fetchUsuarios(page = 1) {
      this.loading = true

      const params = {
        schema: this.schema,
        name: this.nome,
        cpf: this.cpf,
        funcao: this.funcao,
        status: this.status,
        periodoI: this.initialDate,
        periodoF: this.finalDate,
        page,
      }

      this.filters = params

      await this.$http.get(`${urlApiV2}/usuarios`, {
        params,
      })
        .then(response => {
          const data = response.body.dados

          this.usuarios = data.data

          if (this.usuarios.length < 1) {
            this.toast('Nenhum resultado encontrado!')
          }

          this.paginationData = {
            currentPage: data.current_page,
            lastPage: data.last_page,
            total: data.total,
          }
        })
        .catch(err => {
          this.toast('Erro ao buscar alunos.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    async updateStatus(id, status) {
      this.loadingActions = true

      await this.$http.patch(`${urlApi}/usuarios/${id}/${status}`)
        .then(async response => {
          this.toast('Status atualizado!')

          this.fetchUsuarios()
        })
        .catch(err => {
          this.toast('Erro ao atualizar status do usuário.')
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async submitExport(e) {
      this.exportLoading = true

      if (e.format === 'pdf') {
        await this.$http.get(`${urlApiV2}/usuarios/pdf`, {
          params: this.filters
        })
          .then(response => {
            _export.downloadPDF(response.body.dados, "usarios")
          })
          .catch((err) => {
            this.toast("Erro ao exportar o arquivo.")
          })
          .finally(() => {
            this.exportLoading = false
          })
      } else {
        await this.$http.get(`${urlApiV2}/usuarios/xlsx`, {
          params: this.filters
        })
          .then(response => {
            _export.downloadCSV(response.body.dados, "usarios")
          })
          .catch((err) => {
            this.toast("Erro ao exportar o arquivo.")
          })
          .finally(() => {
            this.exportLoading = false
          })
      }

      this.$refs.exportModal.close()
    },
    openInfoModal(id) {
      this.$refs.infoModal.open(id)
    },
    openExportModal() {
      this.$refs.exportModal.open()
    },
    async toEdit(id) {
      this.$router.push({ name: 'editar-usuario', params: { idUsuario: id } })
    },
    clearInputs() {
      this.nome = ''
      this.cpf = ''
      this.funcao = ''
      this.status = ''
      this.initialDate = ''
      this.finalDate = ''
    },
    toast(text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.usuarios-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input {
          flex: 1;
        }
      }
    }

    .buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ECECEC;
        }

        &:not(:last-child) {
          border-right: 1px solid #ECECEC;
        }
      }
    }
  }

  .actions {
    align-self: flex-end;
  }
}
</style>
